import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User, UserRole } from '@tipstrack/shared';
import {
  clearSsoMarker,
  clearToken,
  errorMessage,
  fetchMeRaw,
  getToken,
  isSsoSession,
  setToken,
} from '../api/client.js';
import { fetchCurrentUser, login as loginRequest } from '../api/endpoints.js';
import { oidcEnabled, ssoLogout, userManager } from './oidc.js';
import { bridgeSsoSession, stashSsoError } from './ssoBridge.js';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  hasRole: (...roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // When the portal is configured we always check the OIDC store first, so start in
  // loading to avoid a flash of the signed-out state before the session is verified.
  const [loading, setLoading] = useState<boolean>(oidcEnabled || Boolean(getToken()));

  // On mount, hydrate the session. SSO sessions come from the TIPSSSO portal and are
  // rebuilt from the OIDC store; legacy (password) sessions hydrate from `/auth/me`.
  useEffect(() => {
    let cancelled = false;

    // Legacy (password) session: confirm the stored token against /auth/me.
    function hydrateLegacy() {
      if (!getToken()) {
        if (!cancelled) setLoading(false);
        return;
      }
      fetchCurrentUser()
        .then((u) => {
          if (!cancelled) setUser(u);
        })
        .catch(() => {
          if (!cancelled) {
            clearToken();
            setUser(null);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    if (oidcEnabled && userManager) {
      const um = userManager;

      // Bridge a portal session into the app, but VERIFY it against /auth/me before
      // trusting it. A ZITADEL identity with no matching TipsTrack user (or a rejected
      // token) must NOT produce a fabricated session — that renders the app, 401s on
      // data, redirects to login, re-bridges, and reloads forever. On failure we clear
      // the local OIDC + app session so the user lands signed-out (password login
      // still works) with the rejection reason surfaced on the login page.
      const hydrateSso = (u: Parameters<typeof bridgeSsoSession>[0]) => {
        bridgeSsoSession(u); // stores the access token + sso marker
        fetchMeRaw()
          .then((real) => {
            if (!cancelled) {
              setUser(real);
              setLoading(false);
            }
          })
          .catch((err: unknown) => {
            if (cancelled) return;
            stashSsoError(errorMessage(err));
            clearToken();
            clearSsoMarker();
            setUser(null);
            setLoading(false);
            void um.removeUser().catch(() => {});
          });
      };

      // Keep the user fresh across silent token renewals + on the callback's UserLoaded.
      const onLoaded = (u: Parameters<typeof bridgeSsoSession>[0]) => hydrateSso(u);
      um.events.addUserLoaded(onLoaded);

      um
        .getUser()
        .then((u) => {
          if (cancelled) return;
          if (u && !u.expired) {
            hydrateSso(u);
          } else if (window.location.pathname.startsWith('/auth/')) {
            // The OIDC redirect callback is mid-flight; its UserLoaded event will
            // hydrate the session. Stay in loading rather than redirecting away.
          } else {
            // No portal session — a legacy password session may still be active.
            hydrateLegacy();
          }
        })
        .catch(() => {
          if (!cancelled) hydrateLegacy();
        });

      return () => {
        cancelled = true;
        um.events.removeUserLoaded(onLoaded);
      };
    }

    hydrateLegacy();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await loginRequest(email, password);
    setToken(result.token);
    setUser(result.user);
    return result.user;
  }, []);

  const logout = useCallback(() => {
    const sso = isSsoSession();
    clearToken();
    clearSsoMarker();
    setUser(null);
    // SSO sessions end through the ZITADEL end-session flow (redirects to `${origin}/`).
    if (oidcEnabled && sso) {
      void ssoLogout();
    }
  }, []);

  const hasRole = useCallback(
    (...roles: UserRole[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout, hasRole }),
    [user, loading, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
